import { AuthContext } from "../../context/authContext";
import React, { useContext, useState, useCallback, useEffect } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { useMediaQuery, useTheme, Box, TextField, IconButton, InputAdornment, Grid, Typography } from '@mui/material';
import SearchIcon          from '@mui/icons-material/Search';
import PersonOutlineIcon   from '@mui/icons-material/PersonOutline';
import FavoriteBorderIcon  from '@mui/icons-material/FavoriteBorder';
import FavoriteIcon        from '@mui/icons-material/Favorite';
import LogoutIcon          from '@mui/icons-material/Logout';
import ShoppingBagOutlinedIcon from '@mui/icons-material/ShoppingBagOutlined';
import ListAltIcon         from '@mui/icons-material/ListAlt';
import HomeOutlinedIcon    from '@mui/icons-material/HomeOutlined';
import { DatatableModal }  from "../../pages/cliente/clienteTable/Datatable";
import logoEmpresa         from "../images/logoEmpresa.png";
import LogoutConfirmationModal from "../ConfirmationModal/LogoutConfirmationModal";
import { Menu }            from "./MenuCategorias";

const appUrl = process.env.REACT_APP_URL_BACKEND;

// ─── Logo ─────────────────────────────────────────────────────────────────────
const Logo = () => {
  const { empresa = {} } = useContext(AuthContext);
  return (
    <Box display="flex" alignItems="center" justifyContent="center">
      <Link to="/" style={{ textDecoration: 'none', display: 'flex', alignItems: 'center' }}>
        <img
          src={empresa?.imagen_sitio ? `${appUrl}${empresa.imagen_sitio}` : logoEmpresa}
          alt="Logo"
          style={{ maxWidth: 180, maxHeight: 60, objectFit: 'contain', cursor: 'pointer' }}
        />
      </Link>
    </Box>
  );
};

// ─── SearchBar ────────────────────────────────────────────────────────────────
const SearchBar = ({ searchQuery, onChange, onSearch }) => (
  <Box display="flex" alignItems="center" justifyContent="center" width="100%">
    <TextField
      variant="outlined" placeholder="Que estas buscando?" fullWidth
      value={searchQuery} onChange={onChange}
      onKeyPress={(e) => e.key === 'Enter' && onSearch()} size="small"
      sx={{
        '& .MuiOutlinedInput-root': {
          borderRadius: 'var(--radius-full)', backgroundColor: 'var(--bg-subtle)',
          fontSize: '0.9rem', fontFamily: 'var(--font-body)', transition: 'all 0.25s ease',
          '& fieldset': { border: '2px solid transparent' },
          '&:hover fieldset': { border: '2px solid var(--border-hover)' },
          '&.Mui-focused fieldset': { border: '2px solid var(--accent)' },
          '&.Mui-focused': { backgroundColor: 'var(--bg-card)', boxShadow: '0 0 0 4px rgba(233,69,96,0.1)' },
        },
      }}
      InputProps={{
        startAdornment: (
          <InputAdornment position="start">
            <SearchIcon sx={{ color: 'var(--text-muted)', fontSize: 20 }} />
          </InputAdornment>
        ),
        endAdornment: searchQuery ? (
          <InputAdornment position="end">
            <IconButton size="small" onClick={onSearch}
              sx={{ backgroundColor: 'var(--accent)', color: '#fff', width: 32, height: 32, '&:hover': { backgroundColor: 'var(--accent-hover)' } }}>
              <SearchIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </InputAdornment>
        ) : null,
      }}
    />
  </Box>
);

// ─── NavIconBtn ───────────────────────────────────────────────────────────────
const NavIconBtn = ({ icon, label, active, onClick }) => (
  <Box onClick={onClick} sx={{
    cursor: 'pointer', display: 'flex', flexDirection: 'column', alignItems: 'center',
    gap: '2px', padding: '6px 12px', borderRadius: 'var(--radius-md)',
    backgroundColor: active ? 'var(--accent-soft)' : 'transparent',
    transition: 'all 0.2s ease',
    '&:hover': { backgroundColor: active ? 'var(--accent-soft)' : 'var(--bg-subtle)', transform: 'translateY(-1px)' },
    '&:active': { transform: 'scale(0.96)' },
  }}>
    {React.cloneElement(icon, { sx: { fontSize: 22, color: active ? 'var(--accent)' : 'var(--text-secondary)' } })}
    <Typography variant="caption" sx={{ fontSize: '0.65rem', fontWeight: active ? 700 : 500, color: active ? 'var(--accent)' : 'var(--text-muted)', lineHeight: 1, fontFamily: 'var(--font-body)' }}>
      {label}
    </Typography>
  </Box>
);

// ─── ActionButtons ────────────────────────────────────────────────────────────
const ActionButtons = ({ handleOpenModal, disabledClientes, clienteSelected }) => {
  const { user, isAuthenticated, logout } = useContext(AuthContext);
  const navigate = useNavigate();
  const location = useLocation();
  const [showLogoutModal, setShowLogoutModal] = useState(false);

  const handleClickLogin = () => {
    if (!isAuthenticated) return navigate("/login");
    if ((user?.rol_id === 1 && clienteSelected) || user?.rol_id === 2) navigate("/profile");
  };
  const handleClickCart = () => navigate(isAuthenticated ? "/cart" : "/login");
  const handleClickFav  = () => navigate(isAuthenticated ? "/favourites" : "/login");
  const displayName = !isAuthenticated
    ? "Ingresar"
    : (clienteSelected?.cli_name || user?.sel_firstname || "Perfil").split(" ")[0];

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, justifyContent: 'center' }}>
      {disabledClientes && <NavIconBtn icon={<ListAltIcon />} label="Clientes" onClick={handleOpenModal} />}
      <NavIconBtn icon={<PersonOutlineIcon />} label={displayName} active={location.pathname === "/profile"} onClick={handleClickLogin} />
      <NavIconBtn icon={<ShoppingBagOutlinedIcon />} label="Carrito" active={location.pathname === "/cart"} onClick={handleClickCart} />
      {isAuthenticated && (<>
        <NavIconBtn
          icon={location.pathname === "/favourites" ? <FavoriteIcon /> : <FavoriteBorderIcon />}
          label="Favoritos" active={location.pathname === "/favourites"} onClick={handleClickFav}
        />
        <NavIconBtn icon={<LogoutIcon />} label="Salir" onClick={() => setShowLogoutModal(true)} />
        <LogoutConfirmationModal
          isOpen={showLogoutModal}
          onClose={() => setShowLogoutModal(false)}
          onConfirm={() => { logout(); setShowLogoutModal(false); }}
        />
      </>)}
    </Box>
  );
};

// ─── SearchSection ────────────────────────────────────────────────────────────
const SearchSection = () => {
  const location = useLocation();
  const navigate  = useNavigate();
  const { forceModalOpen, setForceModalOpen, showClientes, clienteSelected } = useContext(AuthContext);
  const [disabledClientes, setDisabledClientes] = useState(showClientes);
  const [searchQuery, setSearchQuery] = useState('');
  const theme    = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  useEffect(() => { setDisabledClientes(showClientes); }, [showClientes]);

  // ── Helper: modifica solo ?q sin tocar r, u, su, m, etc. ─────────────────
  const navigateWithQ = useCallback((q, replace = false) => {
    const params = new URLSearchParams(location.search);
    if (q.trim()) {
      params.set('q', q.trim());
    } else {
      params.delete('q');
    }
    const search = params.toString();
    navigate(`/category${search ? `?${search}` : ''}`, { replace });
  }, [location.search, navigate]);

  // Al escribir: limpiar q de inmediato si quedó vacío ───────────────────────
  const handleInputChange = useCallback((e) => {
    const val = e.target.value;
    setSearchQuery(val);
    if (!val.trim() && location.pathname === '/category') {
      navigateWithQ('', true);
    }
  }, [location.pathname, navigateWithQ]);

  // Al presionar Enter o el botón de buscar ─────────────────────────────────
  const handleSearch = useCallback(() => {
    navigateWithQ(searchQuery);
  }, [searchQuery, navigateWithQ]);

  // Debounce: actualiza ?q mientras escribe, preservando r, u, su, m ─────────
  useEffect(() => {
    if (location.pathname !== '/category') return; // solo actúa en /category
    const delay = setTimeout(() => {
      navigateWithQ(searchQuery, true);
    }, 400);
    return () => clearTimeout(delay);
  }, [searchQuery]); // eslint-disable-line react-hooks/exhaustive-deps
  // nota: intencionalmente excluimos navigateWithQ del array para que el debounce
  // no se re-registre cada vez que cambia location.search

  return (
    <Box sx={{
      position: 'sticky', top: 0, zIndex: 1200,
      backgroundColor: 'rgba(255,255,255,0.92)',
      backdropFilter: 'blur(20px)',
      borderBottom: '1px solid var(--border)',
    }}>
      <Box sx={{ maxWidth: 'var(--container-max)', mx: 'auto', px: { xs: 2, sm: 3, md: 4 } }}>
        <Grid container spacing={2} alignItems="center" justifyContent="space-between" sx={{ py: isMobile ? 1 : 1.5 }}>
          <Grid item xs={3} sm={3} md={2.5}><Logo /></Grid>
          <Grid item xs={9} sm={6} md={6}>
            <SearchBar searchQuery={searchQuery} onChange={handleInputChange} onSearch={handleSearch} />
          </Grid>
          {!isMobile && (
            <Grid item sm={3} md={3.5} display="flex" justifyContent="flex-end">
              <ActionButtons
                handleOpenModal={() => setForceModalOpen(true)}
                disabledClientes={disabledClientes}
                clienteSelected={clienteSelected}
              />
            </Grid>
          )}
        </Grid>
      </Box>
      <DatatableModal open={forceModalOpen} onClose={() => setForceModalOpen(false)} />
    </Box>
  );
};

// ─── MobileBottomBar ──────────────────────────────────────────────────────────
const MobileBottomBar = () => {
  const { forceModalOpen, setForceModalOpen, showClientes, clienteSelected } = useContext(AuthContext);
  const [disabledClientes, setDisabledClientes] = useState(showClientes);
  const theme    = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => { setDisabledClientes(showClientes); }, [showClientes]);

  if (!isMobile) return null;

  return (
    <Box sx={{
      position: 'fixed', bottom: 0, left: 0, right: 0, zIndex: 1300,
      bgcolor: 'rgba(255,255,255,0.97)', backdropFilter: 'blur(20px)',
      borderTop: '1px solid var(--border)',
    }}>
      <Menu mobileOnly />
      <Box sx={{ display: 'flex', justifyContent: 'center', borderTop: '1px solid var(--border)', px: 1, pt: 0.25, pb: 0.5 }}>
        <NavIconBtn icon={<HomeOutlinedIcon />} label="Inicio" active={location.pathname === "/"} onClick={() => navigate("/")} />
        <ActionButtons
          handleOpenModal={() => setForceModalOpen(true)}
          disabledClientes={disabledClientes}
          clienteSelected={clienteSelected}
        />
      </Box>
    </Box>
  );
};

// ─── Navbar ───────────────────────────────────────────────────────────────────
const Navbar = () => (
  <>
    <SearchSection />
    <Box sx={{ display: { xs: 'none', sm: 'block' } }}>
      <Menu />
    </Box>
    <MobileBottomBar />
  </>
);

export default Navbar;